import React, { useEffect, useState } from 'react';
import ReportDownload from '../ReportDownload';
import { normalizeStage, STAGES, eventTeamIds, isActiveTeamUser } from '../../lib/traceability';
import { displayStatus, evaluationPercentage } from '../../lib/evaluationIntegrity';
import { ScoreSummary } from '../EvaluationIntegrity';
import StatusBadge from '../StatusBadge';
import { useNavigate } from 'react-router-dom';
import { eventsAPI, usersAPI } from '../../api/client';
import SemaforoBadge from '../SemaforoBadge';
import { Plus, Eye, FileText, X, Clock, Layers, Users, Edit3, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatLevantadorLabel } from '../../lib/roleHelpers';
import { isTemplateSelectable } from '../../lib/templateStatus';

// #Fase3-C1 — La API espera etapas canónicas también al crear evaluaciones.
const normalizeEvalType = normalizeStage;
const EVAL_TYPES = Object.fromEntries(Object.entries(STAGES).map(([key, label]) => [key, {
  label, bg: 'bg-blue-100 text-blue-800 border-blue-200',
}]));

// #Fase3-G3 — Crear desde el evento conserva plantilla, etapa y pertenencia del levantador.
export function AssignTemplateModal({ eventId, onAssigned, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [event, setEvent] = useState(null);
  const [newMember, setNewMember] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedLev, setSelectedLev] = useState('');
  const [evalType, setEvalType] = useState('antes');
  const [assigning, setAssigning] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      eventsAPI.getAvailableTemplates(eventId).catch(() => ({ data: { items: [] } })),
      usersAPI.list({ limit: 500 }).catch(() => ({ data: { items: [] } })),
      eventsAPI.get(eventId),
    ]).then(([tRes, uRes, eventRes]) => {
      // #Fase0-B3 — available-templates ya filtra 'activa' en la API (y no
      // devuelve status); por si algún día lo devuelve, aquí tampoco se
      // ofrece una retirada/borrador para crear evaluaciones nuevas.
      const tItems = (tRes.data?.items || tRes.data || []).filter(t => !t.status || isTemplateSelectable(t));
      setTemplates(tItems);
      // Pre-seleccionar la plantilla default si existe
      const def = tItems.find((t) => t.is_default);
      if (def) setSelectedTemplate(def.id);
      const uItems = uRes.data?.items || uRes.data || [];
      setUsers(uItems);
      setEvent(eventRes.data);
    })
      .catch(() => toast.error('Error al cargar datos del formulario'))
      .finally(() => setLoading(false));
  }, [eventId]);

  // #Fase3-G3 — Asignar una evaluación no concede acceso a los recursos del evento por sí solo.
  const teamIds = eventTeamIds(event);
  const levantadores = users.filter((user) => isActiveTeamUser(user) && teamIds.includes(user.id));
  const outsideTeam = users.filter((user) => isActiveTeamUser(user) && !teamIds.includes(user.id));
  const addMember = async () => {
    if (!newMember) return;
    setAssigning(true);
    try {
      const { data: current } = await eventsAPI.get(eventId);
      const assigned_user_ids = [...new Set([...(current.assigned_user_ids || []), newMember])];
      await eventsAPI.update(eventId, { assigned_user_ids });
      setEvent({ ...current, assigned_user_ids });
      setSelectedLev(newMember);
      setNewMember('');
    } catch {
      toast.error('No se pudo agregar al equipo. La evaluación no se ha creado.');
    } finally {
      setAssigning(false);
    }
  };

  const handleAssign = async (e) => {
    e?.preventDefault?.();
    if (!selectedTemplate || !event || (selectedLev && !teamIds.includes(selectedLev))) return;
    setAssigning(true);
    try {
      await eventsAPI.assignTemplate(eventId, {
        template_id: selectedTemplate,
        evaluation_type: evalType,
        assigned_levantador_id: selectedLev || null,
      });
      toast.success('Evaluación creada');
      onAssigned();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear evaluación');
    } finally { setAssigning(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="assign-template-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy sticky top-0 z-10">
          <h3 className="font-bold text-white">Nueva Evaluación</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <form onSubmit={handleAssign} className="p-5 space-y-4">
          {/* Template selector (radio buttons) */}
          <div>
            <label className="soem-label block mb-1.5 flex items-center gap-1">
              <Layers size={12} /> Plantilla de evaluación <span className="text-red-500">*</span>
            </label>
            {loading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-slate-100 animate-pulse rounded-sm" />)}
              </div>
            ) : templates.length === 0 ? (
              <p className="text-slate-400 text-sm p-3 bg-slate-50 rounded-sm border border-slate-200">
                No hay plantillas activas disponibles para este evento.
              </p>
            ) : (
              <div className="space-y-2">
                {templates.map(t => {
                  const qCount = (t.sections || []).reduce((sum, s) => sum + (s.questions || []).length, 0)
                    || t.questions_count
                    || t.question_count
                    || 0;
                  const sCount = (t.sections || []).length || t.sections_count || t.section_count || 0;
                  return (
                    <label key={t.id}
                      className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${selectedTemplate === t.id ? 'border-soem-primary bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input type="radio" name="template" value={t.id} checked={selectedTemplate === t.id}
                        onChange={() => setSelectedTemplate(t.id)} className="mt-0.5" />
                      <div>
                        <p className="font-semibold text-soem-navy text-sm flex items-center gap-2">
                          {t.name}
                          {t.is_default && <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded font-bold">Por defecto</span>}
                        </p>
                        <p className="text-slate-400 text-xs mt-0.5">
                          {sCount} secciones · {qCount} preguntas
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tipo de evaluación */}
          <div>
            <label htmlFor="f-tipo-eval" className="soem-label block mb-1.5">Tipo de evaluación</label>
            <select id="f-tipo-eval" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              value={evalType} onChange={e => setEvalType(e.target.value)} data-testid="eval-type-select">
              {Object.entries(EVAL_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          {/* Asignar levantador (opcional) */}
          {levantadores.length > 0 && (
            <div>
              <label htmlFor="f-levantador" className="soem-label block mb-1.5 flex items-center gap-1">
                <Users size={12} /> Asignar levantador (opcional)
              </label>
              <select id="f-levantador" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                value={selectedLev} onChange={e => setSelectedLev(e.target.value)}>
                <option value="">— Sin asignar específico —</option>
                {levantadores.map(u => <option key={u.id} value={u.id}>{formatLevantadorLabel(u)}</option>)}
              </select>
            </div>
          )}

          <label className="block text-sm">Agregar al equipo
            <select className="block w-full border p-2" value={newMember}
              disabled={assigning || !event} onChange={(e) => setNewMember(e.target.value)}>
              <option value="">Selecciona levantador activo</option>
              {outsideTeam.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
            </select>
          </label>
          <button type="button" disabled={!newMember || assigning} onClick={addMember}>Agregar al equipo</button>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={!selectedTemplate || !event || assigning}
              className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="assign-btn">
              <Plus size={14} /> {assigning ? 'Creando...' : 'Crear Evaluación'}
            </button>
            <button type="button" onClick={onClose} className="soem-btn-secondary flex-1 py-2.5 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EvaluationsTab({ eventId, canEdit }) {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);

  const load = () => {
    setLoading(true);
    eventsAPI.getEvaluations(eventId)
      .then(r => setEvaluations(r.data?.items || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [eventId]);


  // #Fase3-C1 — Agrupar por etapa canónica evita ocultar evaluaciones anteriores.
  const grouped = {};
  for (const ev of evaluations) {
    const type = normalizeEvalType(ev.evaluation_type || ev.type);
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(ev);
  }

  return (
    <div className="space-y-5" data-testid="evaluations-tab">
      <div className="flex items-center justify-between">
        <p className="soem-label">Evaluaciones del evento</p>
        {canEdit && (
          <button onClick={() => setShowAssign(true)} className="soem-btn-primary px-3 py-2 text-xs flex items-center gap-1.5" data-testid="assign-template-btn">
            <Plus size={14} /> Nueva Evaluación
          </button>
        )}
      </div>
      {loading ? (
        <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className="soem-card h-16 animate-pulse bg-slate-100" />)}</div>
      ) : evaluations.length === 0 ? (
        <div className="soem-card p-10 text-center">
          <FileText size={36} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No hay evaluaciones asignadas a este evento.</p>
          {canEdit && <p className="text-slate-400 text-xs mt-1">Usa "Asignar Plantilla" para crear la primera.</p>}
        </div>
      ) : (
        Object.entries(grouped).map(([type, evals]) => {
          const cfg = EVAL_TYPES[type] || EVAL_TYPES.otro;
          return (
            <div key={type}>
              <p className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border mb-3 ${cfg.bg}`}>{cfg.label}</p>
              <div className="space-y-2">
                {evals.map(ev => (
                  <div key={ev.id} className="soem-card p-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid={`eval-row-${ev.id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <SemaforoBadge percentage={evaluationPercentage(ev)} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-soem-navy truncate">{ev.template_name || 'Evaluacion'} v{ev.version || 1}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {/* #EvalCompletionBadge — antes mostraba sólo "borrador"
                              aunque el levantador hubiera contestado todo. Ahora
                              si el backend reporta is_complete=true Y status sigue
                              en borrador, mostramos badge verde "Completada" para
                              que el lider sepa que está lista para enviar a revisión. */}
                          {ev.is_complete && (ev.status === 'borrador' || !ev.status) ? (
                            <span className="text-xs font-semibold inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 rounded-full border border-green-200">
                              <CheckCircle2 size={11} /> Completada — lista para enviar
                            </span>
                          ) : (
                            <>
                              <StatusBadge status={displayStatus(ev)} />
                              <ScoreSummary evaluation={ev} />
                            </>
                          )}
                          {/* #EvalStartedAt — fecha y hora en que la evaluacion
                              se inició (created_at del documento). Antes sólo
                              se veía el status sin contexto temporal. */}
                          {ev.created_at && (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                              <Clock size={11} />
                              {new Date(ev.created_at).toLocaleString('es-MX', {
                                day: 'numeric', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* #ContestarFromEvent — el botón cambia según el
                          estado de la eval. Borrador/Regresada → Contestar
                          (lleva al checklist editable). En Revisión →
                          Revisar (pantalla de aprobación del líder).
                          Publicada/Archivada → Ver (read-only). Antes
                          siempre llevaba a /revision aunque la eval
                          estuviera en borrador, dejando al usuario sin
                          forma de contestar. */}
                      {(() => {
                        const editable = ev.status === 'borrador' || ev.status === 'regresada' || !ev.status;
                        const isReview = ev.status === 'en_revision';
                        const label = editable ? 'Contestar' : isReview ? 'Revisar' : 'Ver';
                        const Icon = editable ? Edit3 : isReview ? CheckCircle2 : Eye;
                        const target = editable
                          ? `/dashboard/evaluaciones/${ev.id}/checklist`
                          : `/dashboard/evaluaciones/${ev.id}/revision`;
                        const cls = editable
                          ? 'soem-btn-primary px-2.5 py-1.5 text-xs flex items-center gap-1'
                          : 'soem-btn-secondary px-2.5 py-1.5 text-xs flex items-center gap-1';
                        return (
                          <button onClick={() => navigate(target)} className={cls} data-testid={`open-eval-${ev.id}`}>
                            <Icon size={12} /> {label}
                          </button>
                        );
                      })()}
                      <ReportDownload evaluation={ev} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
      {showAssign && <AssignTemplateModal eventId={eventId} onAssigned={load} onClose={() => setShowAssign(false)} />}
    </div>
  );
}
